/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * DailyAttachModal — the organizer's "attach runs" dialog on a multi-day event.
 *
 * PAGED ON THE SERVER. A busy day can hold tens of thousands of runs, so the
 * dialog never pulls a whole day into the browser: it asks for one page of
 * DAILY_ATTACH_PAGE_SIZE runs at a time and shows the day's totals the server
 * counted. One request per page turn also keeps it well inside the analytics
 * throttle, which a read-everything loop was not.
 *
 * SEARCH IS A VIEW, NOT A SELECTION. It narrows the rows on screen (on the
 * server, by handle or entry name); the ticks and "N selected" still cover the
 * whole day, so finding one athlete to untick never drops everybody else.
 *
 * EVERYTHING STARTS TICKED, as the inline panel did: the organizer unticks
 * the runs to leave out, on any page, and those unticks are kept by run id
 * across page turns. "Attach selected" is the day's total minus the unticked.
 * The header checkbox is for the WHOLE DAY, every page: unticking it leaves
 * nothing selected, and single runs can then be ticked back in by hand.
 * Pages never opened are read in full only when Attach is pressed.
 *
 * WHILE ATTACHING, a progress panel covers the list with the total, attached,
 * failed and remaining counts as each batch comes back. Nothing closes the
 * dialog until every request has answered (Cancel, ×, Escape and the backdrop
 * are all refused, and leaving the page asks first); the panel then stays up
 * with the outcome until the organizer dismisses it.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchDailyAttachPage, fetchDailyAttachables, attachDailyRuns, DAILY_ATTACH_PAGE_SIZE,
} from './dailyAttachApi';
import { fetchKnownNames, titleCase } from './attachApi';
import { fetchTimingSources, timingSourceIdByCode, APPLICATION_TRACKED } from './resultsApi';
import { formatClock, formatKm, formatPace } from './boardModel';
import { eventDayDate } from './dailyMode';

// The board's own palette (LeaderboardPage, EventDayPicker): flame orange for
// what is on, its deep ink for orange text, a warm wash behind picked rows.
const INK = '#241708';
const MUTED = '#6E6259';
const FLAME = '#FF6A1A';
const FLAME_INK = '#9A3412';
const SOFT = '#FFF4EC';
const HAIR = '#EEE8E2';
const PAPER = '#FBF8F5';
const GOOD = '#1B7F48';
const BAD = '#B42318';
const DISPLAY = 'Archivo, Inter, system-ui, sans-serif';

export const numberFmt = new Intl.NumberFormat('en-IN');
export const SEARCH_DELAY_MS = 300;

/** "Fri 30 Oct" for day `n` of the event. */
export function dayCaption(n, eventStart, timezone) {
  const iso = eventDayDate(n, eventStart, timezone);
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC',
  });
}

export function nameOf(row, knownNames) {
  const known = knownNames && knownNames.get(String(row.athleteUserId));
  if (known) return known;
  if (row.athleteHandle) return titleCase(row.athleteHandle);
  return `Athlete ${row.athleteUserId}`;
}

export function initialsOf(name) {
  return String(name || '?').split(/\s+/).filter(Boolean).slice(0, 2)
    .map((w) => w[0].toUpperCase()).join('') || '?';
}

export function startedAt(value) {
  if (!value) return '—';
  const at = new Date(`${String(value).replace(' ', 'T').replace(/Z?$/, 'Z')}`);
  if (Number.isNaN(at.getTime())) return '—';
  return at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** Page numbers to draw: first, last, and a window round the current one. */
export function pageWindow(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out = [1];
  const from = Math.max(2, current - 1);
  const to = Math.min(total - 1, current + 1);
  if (from > 2) out.push('…');
  for (let n = from; n <= to; n += 1) out.push(n);
  if (to < total - 1) out.push('…');
  out.push(total);
  return out;
}

// Exported so RemoveAttachedModal renders with the SAME rules, not a copy that
// can drift. Class names stay dam-* on purpose: one stylesheet, one look.
//
// The doubled `export` here was a merge artifact — both sides added the keyword
// — and it was one of the two syntax errors that stopped the branch building.
export const CSS = `
.dam-backdrop{position:fixed;inset:0;z-index:1200;display:flex;align-items:center;justify-content:center;
  padding:24px;background:rgba(24,14,4,.55);backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);
  animation:dam-fade .16s ease-out}
.dam-dialog{position:relative;width:100%;max-width:1040px;height:min(88vh,860px);display:flex;flex-direction:column;
  background:#fff;color:${INK};border-radius:24px;overflow:hidden;
  box-shadow:0 30px 80px rgba(24,14,4,.35),0 2px 8px rgba(24,14,4,.12);animation:dam-rise .2s ease-out}
.dam-dialog:focus{outline:none}
.dam-head{padding:22px 24px 16px;border-bottom:1px solid ${HAIR};background:#fff}
.dam-kicker{margin:0;font-size:12px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:${FLAME_INK}}
.dam-title{margin:4px 0 0;font-family:${DISPLAY};font-size:24px;font-weight:800;letter-spacing:-.02em}
.dam-x{width:44px;height:44px;border-radius:12px;border:1px solid ${HAIR};background:#fff;color:${INK};
  font-size:22px;line-height:1;cursor:pointer;flex:none}
.dam-x:disabled{opacity:.4;cursor:not-allowed}
.dam-tools{display:flex;flex-wrap:wrap;gap:10px;margin-top:16px;align-items:center}
.dam-search{position:relative;flex:1 1 260px;min-width:0;display:flex;align-items:center}
.dam-search svg{position:absolute;left:14px;pointer-events:none}
.dam-search input{width:100%;min-height:44px;padding:0 14px 0 42px;border-radius:12px;box-sizing:border-box;
  border:1px solid rgba(36,23,8,.16);background:#fff;font-size:15px;color:${INK}}
.dam-search input:focus{outline:2px solid ${FLAME};outline-offset:1px;border-color:transparent}
.dam-select{min-height:44px;border-radius:12px;border:1px solid rgba(36,23,8,.16);padding:0 12px;font-size:14px;
  font-weight:600;color:${INK};background:#fff}
.dam-stats{display:flex;flex-wrap:wrap;gap:8px;margin-top:12px;align-items:center}
.dam-chip{display:inline-flex;align-items:baseline;gap:6px;padding:7px 12px;border-radius:999px;
  background:#fff;border:1px solid ${HAIR};font-size:13px;color:${MUTED}}
.dam-chip b{color:${INK};font-size:15px;font-variant-numeric:tabular-nums}
.dam-chip.on{background:${FLAME};border-color:${FLAME};color:#fff}
.dam-chip.on b{color:#fff}
.dam-body{flex:1;overflow:auto;background:${PAPER}}
.dam-table{width:100%;border-collapse:separate;border-spacing:0;font-size:14px;table-layout:fixed}
.dam-table th{position:sticky;top:0;z-index:1;background:#fff;text-align:left;font-size:11px;letter-spacing:.08em;
  text-transform:uppercase;color:${MUTED};font-weight:700;padding:12px 16px;border-bottom:1px solid ${HAIR};white-space:nowrap}
.dam-table td{padding:10px 16px;border-bottom:1px solid ${HAIR};background:#fff;font-variant-numeric:tabular-nums;
  vertical-align:middle;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
/* QUALIFIED WITH th/td ON PURPOSE: ".dam-table th" scores (0,1,1) and a bare
   ".dam-num" only (0,1,0), so an unqualified rule loses and every Distance /
   Time / Pace heading sits left while its figures are right — measured 64–79px
   adrift before this was qualified. */
.dam-table th.dam-num,.dam-table td.dam-num{text-align:right}
.dam-table th.dam-check,.dam-table td.dam-check{text-align:center;padding-left:12px;padding-right:4px}
.dam-table tr.picked td{background:${SOFT}}
.dam-table tbody tr{cursor:pointer}
.dam-table tbody tr:hover td{background:#FFEBDD}
.dam-table input[type=checkbox]{width:18px;height:18px;margin:0;accent-color:${FLAME};cursor:pointer;vertical-align:middle}
.dam-who{display:flex;align-items:center;gap:10px;min-width:0}
.dam-who span:last-child{overflow:hidden;text-overflow:ellipsis;font-weight:700}
.dam-avatar{width:32px;height:32px;border-radius:50%;flex:none;display:inline-flex;align-items:center;justify-content:center;
  background:#F6F2EE;color:#4A3D33;font-size:12px;font-weight:800}
.dam-state{padding:56px 24px;text-align:center;color:${MUTED}}
.dam-foot{display:flex;flex-wrap:wrap;gap:12px;align-items:center;justify-content:space-between;
  padding:14px 24px;border-top:1px solid ${HAIR};background:#fff}
.dam-pager{display:flex;gap:4px;align-items:center;flex-wrap:wrap}
.dam-pg{min-width:38px;height:38px;padding:0 10px;border-radius:10px;border:1px solid ${HAIR};background:#fff;
  color:${INK};font-size:13px;font-weight:700;cursor:pointer;font-variant-numeric:tabular-nums}
.dam-pg:disabled{color:#B9AA98;cursor:not-allowed}
.dam-pg.cur{background:${INK};border-color:${INK};color:#fff}
.dam-btn{min-height:46px;padding:0 22px;border-radius:12px;font-weight:700;font-size:15px;cursor:pointer}
.dam-btn.ghost{background:#fff;border:1px solid rgba(36,23,8,.16);color:${INK}}
.dam-btn.main{background:${FLAME};border:0;color:#fff;box-shadow:0 8px 20px -8px rgba(255,106,26,.7)}
.dam-btn.dark{background:${INK};border:0;color:#fff}
.dam-btn:disabled{opacity:.45;cursor:not-allowed;box-shadow:none}
.dam-link{border:0;background:none;color:${FLAME_INK};font-weight:700;font-size:13px;cursor:pointer;padding:0;min-height:32px}
.dam-cover{position:absolute;inset:0;z-index:5;display:flex;align-items:center;justify-content:center;padding:24px;
  background:rgba(255,255,255,.94);backdrop-filter:blur(2px)}
.dam-card{width:100%;max-width:560px;background:#fff;border:1px solid ${HAIR};border-radius:24px;padding:28px;
  box-shadow:0 1px 2px rgba(36,23,8,.04),0 18px 44px -24px rgba(36,23,8,.35)}
.dam-spin{width:44px;height:44px;border-radius:50%;border:4px solid #FFE4D3;border-top-color:${FLAME};
  animation:dam-turn .9s linear infinite;flex:none}
.dam-tiles{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin:22px 0 16px}
.dam-tile{border:1px solid ${HAIR};border-radius:16px;padding:12px 14px;background:${PAPER}}
.dam-tile dt{margin:0;font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:${MUTED}}
.dam-tile dd{margin:4px 0 0;font-family:${DISPLAY};font-size:22px;font-weight:800;font-variant-numeric:tabular-nums}
.dam-track{height:10px;border-radius:999px;background:#FFE4D3;overflow:hidden;display:flex}
.dam-track>i{display:block;height:100%;transition:width .25s}
.dam-fails{margin:16px 0 0;padding:0;list-style:none;max-height:150px;overflow:auto;border-top:1px solid ${HAIR}}
.dam-fails li{padding:8px 0;border-bottom:1px solid ${HAIR};font-size:13px;color:${BAD}}
@keyframes dam-fade{from{opacity:0}to{opacity:1}}
@keyframes dam-rise{from{opacity:0;transform:translateY(12px) scale(.985)}to{opacity:1;transform:none}}
@keyframes dam-turn{to{transform:rotate(360deg)}}
@media (prefers-reduced-motion:reduce){.dam-spin{animation-duration:2.4s}}
@media (max-width:720px){
  .dam-backdrop{padding:0}
  .dam-dialog{height:100%;max-width:none;border-radius:0}
  .dam-head{padding:16px}
  .dam-foot{padding:12px 16px}
  .dam-hide-sm{display:none}
  .dam-table td,.dam-table th{padding:10px 8px}
  .dam-tiles{grid-template-columns:repeat(2,1fr)}
  .dam-card{padding:20px}
}
`;

export function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={MUTED} strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l4 4" />
    </svg>
  );
}

/**
 * The panel over the list while an attach runs, and its outcome after.
 * `noun` names what is attached ("run", "result"); `children` go under the
 * outcome, above Done (the single-day modal puts its undo list there).
 */
export function AttachProgress({
  progress, report, selected, onDone, noun = 'run', children = null,
}) {
  const finished = Boolean(report);
  const total = finished
    ? report.attached.length + report.refused.length
    : (progress && progress.phase === 'attach' ? progress.total : selected);
  const attached = finished ? report.attached.length : (progress?.attached || 0);
  const failed = finished ? report.refused.length : (progress?.failed || 0);
  const remaining = Math.max(0, total - attached - failed);
  const gathering = !finished && progress?.phase === 'gather';
  const pctOf = (n) => (total ? `${(n / total) * 100}%` : '0%');

  const nouns = `${noun}s`;
  let heading = `Attaching ${nouns}…`;
  let note = 'Keep this window open. It closes only when every request has finished.';
  if (gathering) {
    heading = `Collecting the ${nouns} to attach…`;
    note = `Read ${numberFmt.format(progress.done)} of ${numberFmt.format(progress.total)}. Nothing is written until this is done.`;
  } else if (finished && failed === 0) {
    heading = `All ${nouns} attached`;
    note = `${numberFmt.format(attached)} ${attached === 1 ? noun : nouns} added to the board.`;
  } else if (finished) {
    heading = attached ? 'Finished, with some failures' : 'Nothing was attached';
    note = `The failed ${nouns} are still in the list and can be attached again.`;
  }

  return (
    <div className="dam-cover">
      <section className="dam-card" role={finished ? 'status' : 'progressbar'} aria-live="polite"
        aria-valuemin={finished ? undefined : 0} aria-valuemax={finished ? undefined : total}
        aria-valuenow={finished ? undefined : attached + failed}
        aria-label={finished ? undefined : `Attaching ${nouns}`}
      >
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          {!finished && <span className="dam-spin" aria-hidden="true" />}
          {finished && (
            <span aria-hidden="true" style={{
              width: 44, height: 44, borderRadius: '50%', flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: failed ? '#FDECEA' : '#E7F5EC', color: failed ? BAD : GOOD,
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                {failed ? <path d="M12 8v5M12 16.5v.5" /> : <path d="M20 6 9 17l-5-5" />}
              </svg>
            </span>
          )}
          <div style={{ minWidth: 0 }}>
            <h3 style={{ margin: 0, fontFamily: DISPLAY, fontSize: 20, fontWeight: 800, letterSpacing: '-.01em' }}>{heading}</h3>
            <p style={{ margin: '4px 0 0', fontSize: 14, color: MUTED }}>{note}</p>
          </div>
        </div>

        <dl className="dam-tiles">
          <div className="dam-tile"><dt id="dam-t-total">To attach</dt><dd aria-labelledby="dam-t-total">{numberFmt.format(total)}</dd></div>
          <div className="dam-tile"><dt id="dam-t-ok">Attached</dt><dd aria-labelledby="dam-t-ok" style={{ color: GOOD }}>{numberFmt.format(attached)}</dd></div>
          <div className="dam-tile"><dt id="dam-t-bad">Failed</dt><dd aria-labelledby="dam-t-bad" style={{ color: failed ? BAD : INK }}>{numberFmt.format(failed)}</dd></div>
          <div className="dam-tile"><dt id="dam-t-left">Remaining</dt><dd aria-labelledby="dam-t-left">{numberFmt.format(remaining)}</dd></div>
        </dl>

        <div className="dam-track" aria-hidden="true">
          {gathering
            ? <i style={{ width: progress.total ? `${(progress.done / progress.total) * 100}%` : '0%', background: '#FFB98F' }} />
            : (
              <>
                <i style={{ width: pctOf(attached), background: FLAME }} />
                <i style={{ width: pctOf(failed), background: BAD }} />
              </>
            )}
        </div>

        {finished && report.refused.length > 0 && (
          <ul className="dam-fails" aria-label="Runs that failed">
            {report.refused.slice(0, 200).map((r, i) => (
              <li key={`${r.activityId}-${i}`}>{r.athleteName ? `${r.athleteName}: ` : ''}{r.reason}</li>
            ))}
            {report.refused.length > 200 && (
              <li style={{ color: MUTED }}>…and {numberFmt.format(report.refused.length - 200)} more.</li>
            )}
          </ul>
        )}

        {finished && children}

        {finished && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
            <button type="button" className="dam-btn dark" onClick={onDone}>Done</button>
          </div>
        )}
      </section>
    </div>
  );
}

export default function DailyAttachModal({
  api, eventActivityId, eventStart, eventTimezone, lastDay, initialDay,
  minDistanceMeters = 0, onClose,
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef(null);
  const [day, setDay] = useState(initialDay);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [term, setTerm] = useState('');
  // Selection = a base ("every run" or "no run") plus the runs flipped from
  // it by hand, keyed by run id with the row, kept across pages. Only the
  // flips are stored, so a 50k-run day costs nothing until it is attached.
  const [allBase, setAllBase] = useState(true);
  const [flipped, setFlipped] = useState(() => new Map());
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [report, setReport] = useState(null);
  // The day's totals as last counted. A search that finds nothing answers no
  // rows and so no totals; the day still has them.
  const [dayTotals, setDayTotals] = useState({ runs: 0, entrants: 0 });

  // A new day is a new list: start it on page 1 with everything ticked.
  useEffect(() => {
    setPage(1); setAllBase(true); setFlipped(new Map()); setReport(null);
    setSearch(''); setTerm(''); setDayTotals({ runs: 0, entrants: 0 });
  }, [day]);

  // Typing settles before it asks the server. Only a CHANGED search goes back
  // to page 1 — firing on open too threw a quick page turn back to the start.
  useEffect(() => {
    const next = search.trim();
    if (next === term) return undefined;
    const id = setTimeout(() => { setTerm(next); setPage(1); }, SEARCH_DELAY_MS);
    return () => clearTimeout(id);
  }, [search, term]);

  const listing = useQuery({
    queryKey: ['board', 'daily-attach-page', eventActivityId, day, minDistanceMeters, term, page],
    // The page travels with its rows: while the next page loads, the previous
    // one stays on screen and the "Showing a–b" line must describe IT.
    queryFn: async ({ signal }) => ({
      ...(await fetchDailyAttachPage(api, {
        eventActivityId, dayNumber: day, minDistanceMeters, page, search: term, signal,
      })),
      page,
    }),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const rows = useMemo(() => (listing.data ? listing.data.rows : []), [listing.data]);
  // Read straight off the page whenever it carries them — a total that lagged
  // a render behind made the header box flip the wrong way.
  const fresh = listing.data && listing.data.rows.length
    ? { runs: listing.data.totalRuns, entrants: listing.data.totalEntrants }
    : null;
  useEffect(() => {
    if (fresh) setDayTotals(fresh);
  }, [fresh?.runs, fresh?.entrants]); // eslint-disable-line react-hooks/exhaustive-deps
  let totals = fresh || dayTotals;
  // An empty first page with no search is an empty day, not a stale total.
  if (!fresh && listing.data && !term && page === 1) totals = { runs: 0, entrants: 0 };
  const totalRuns = totals.runs;
  const totalEntrants = totals.entrants;
  const shownTotal = term ? (listing.data ? listing.data.totalMatches : 0) : totalRuns;
  const pages = Math.max(1, Math.ceil(shownTotal / DAILY_ATTACH_PAGE_SIZE));

  // A page emptied by an attach (its runs are now on the board) steps back.
  useEffect(() => {
    if (!listing.isPlaceholderData && listing.data && !rows.length && page > 1) setPage((p) => p - 1);
  }, [listing.isPlaceholderData, listing.data, rows.length, page]);

  const userIds = useMemo(
    () => [...new Set(rows.map((r) => r.athleteUserId))].sort((a, b) => a - b),
    [rows],
  );
  const names = useQuery({
    queryKey: ['board', 'known-names', userIds.join(',')],
    queryFn: ({ signal }) => fetchKnownNames(api, { userIds, signal }),
    enabled: userIds.length > 0,
  });
  const sources = useQuery({
    queryKey: ['board', 'timing-sources'],
    queryFn: ({ signal }) => fetchTimingSources(api, { signal }),
  });

  // Escape closes, the page behind does not scroll, focus moves in and back.
  useEffect(() => {
    const before = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (dialogRef.current) dialogRef.current.focus();
    return () => {
      document.body.style.overflow = overflow;
      if (before && before.focus) before.focus();
    };
  }, []);
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && !busy) onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);
  // Leaving the page mid-attach would cut the remaining requests off.
  useEffect(() => {
    if (!busy) return undefined;
    function onLeave(e) { e.preventDefault(); e.returnValue = ''; }
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, [busy]);

  const isTicked = (id) => (allBase ? !flipped.has(id) : flipped.has(id));
  const selected = allBase ? Math.max(0, totalRuns - flipped.size) : flipped.size;
  const allTicked = totalRuns > 0 && selected === totalRuns;

  function toggle(row) {
    setFlipped((was) => {
      const next = new Map(was);
      if (next.has(row.run.id)) next.delete(row.run.id); else next.set(row.run.id, row);
      return next;
    });
  }

  /** The header box: every run on the day, all pages, on or off. */
  function toggleAll() {
    setAllBase(!allTicked);
    setFlipped(new Map());
  }

  /**
   * Every ticked run of the day as attach picks. One page on screen is the
   * whole day only when the day fits on it and no search is narrowing it;
   * otherwise the day is read in full (500 runs a read, waiting out the
   * throttle) before anything is written, because attaching shifts the
   * offsets a page-by-page read would rely on.
   */
  async function gatherPicks() {
    // Nothing ticked but a few by hand: those rows are already in hand.
    if (!allBase) return withNames([...flipped.values()]);
    let found = rows;
    if (term || totalRuns > rows.length) {
      setProgress({ phase: 'gather', done: 0, total: totalRuns });
      const people = await fetchDailyAttachables(api, {
        eventActivityId, dayNumber: day, minDistanceMeters, strict: true,
        onProgress: (done) => setProgress({ phase: 'gather', done: Math.min(done, totalRuns), total: totalRuns }),
      });
      found = [];
      people.forEach((p) => p.runs.forEach((run) => found.push({
        registrationId: p.registrationId,
        athleteUserId: p.athleteUserId,
        athleteHandle: p.athleteHandle,
        resultId: p.resultId,
        maxSplitNumber: p.maxSplitNumber,
        run,
      })));
    }
    return withNames(found.filter((row) => !flipped.has(row.run.id)));
  }

  async function withNames(ticked) {
    // A name is only WRITTEN for an entrant who has no entry yet (the entry
    // row carries it); everyone else's is just for the report. So only those
    // are looked up, and a failed lookup falls back to the handle rather than
    // stopping the attach — on a 20k-run day this used to be one request
    // naming every athlete on the day.
    const missing = [...new Set(ticked
      .filter((r) => r.resultId === null || r.resultId === undefined)
      .map((r) => r.athleteUserId))]
      .filter((id) => !(names.data && names.data.has(String(id))));
    let more = new Map();
    if (missing.length) {
      try { more = await fetchKnownNames(api, { userIds: missing }); } catch (e) { /* handle fallback */ }
    }
    const known = new Map([...(names.data || new Map()), ...more]);
    return ticked.map((row) => ({
      registrationId: row.registrationId,
      resultId: row.resultId,
      maxSplitNumber: row.maxSplitNumber,
      athleteName: nameOf(row, known),
      dayNumber: day,
      run: row.run,
    }));
  }

  async function onAttach() {
    setBusy(true);
    setReport(null);
    setProgress({ phase: 'gather', done: 0, total: selected });
    try {
      const picks = await gatherPicks();
      setProgress({ phase: 'attach', done: 0, total: picks.length, attached: 0, failed: 0 });
      const out = await attachDailyRuns(api, {
        picks,
        eventActivityId,
        timingSourceId: timingSourceIdByCode(sources.data, APPLICATION_TRACKED),
        minDistanceMeters,
        onProgress: (p) => setProgress({ phase: 'attach', ...p }),
      });
      setReport(out);
      // Hand-picked runs that went in are gone from the list; drop their ticks
      // so the count does not keep them. (In "every run" mode the flips are
      // unticks of runs still waiting, so they stay.)
      if (!allBase) {
        setFlipped((was) => {
          const next = new Map(was);
          out.attached.forEach((id) => next.delete(id));
          return next;
        });
      }
    } catch (e) {
      setReport({ attached: [], refused: [{ activityId: '', athleteName: '', reason: (e && e.message) || 'Could not attach these runs.' }] });
    } finally {
      setBusy(false);
      setProgress(null);
      queryClient.invalidateQueries({ queryKey: ['board', 'daily-attach-page', eventActivityId] });
      queryClient.invalidateQueries({ queryKey: ['board', 'daily-attachable', eventActivityId] });
      queryClient.invalidateQueries({ queryKey: ['board', 'daily', eventActivityId] });
      queryClient.invalidateQueries({ queryKey: ['board', 'race-totals-page', eventActivityId] });
    }
  }

  const shownPage = listing.data ? listing.data.page : page;
  const firstShown = rows.length ? (shownPage - 1) * DAILY_ATTACH_PAGE_SIZE + 1 : 0;
  const lastShown = rows.length ? firstShown + rows.length - 1 : 0;
  const days = Array.from({ length: lastDay }, (_, i) => i + 1);
  const showPanel = busy || Boolean(report);

  return createPortal((
    <div
      className="dam-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <style>{CSS}</style>
      <div
        ref={dialogRef}
        className="dam-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dam-title"
        aria-busy={busy}
        tabIndex={-1}
      >
        <header className="dam-head">
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div style={{ minWidth: 0 }}>
              <p className="dam-kicker">Day {day} of {lastDay}</p>
              <h2 id="dam-title" className="dam-title">Attach runs</h2>
              <p style={{ margin: '4px 0 0', fontSize: 14, color: MUTED }}>
                Every run is ticked. Untick the ones to leave out — on any page.
              </p>
            </div>
            <button type="button" className="dam-x" aria-label="Close" onClick={onClose} disabled={busy}>×</button>
          </div>

          <div className="dam-tools">
            <div className="dam-search">
              <SearchIcon />
              <label htmlFor="dam-search" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
                Search athletes by name
              </label>
              <input
                id="dam-search"
                type="search"
                value={search}
                disabled={busy}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search athletes by name"
                autoComplete="off"
              />
            </div>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: MUTED }}>
              Event day
              <select
                className="dam-select"
                value={day}
                disabled={busy}
                onChange={(e) => setDay(Number(e.target.value))}
              >
                {days.map((n) => (
                  <option key={n} value={n}>{`Day ${n} · ${dayCaption(n, eventStart, eventTimezone)}`}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="dam-stats">
            <span className="dam-chip" aria-label="Total runs"><b>{numberFmt.format(totalRuns)}</b> runs</span>
            <span className="dam-chip"><b>{numberFmt.format(totalEntrants)}</b> entrants</span>
            <span className={`dam-chip${selected ? ' on' : ''}`}><b>{numberFmt.format(selected)}</b> selected</span>
            {term && listing.data && (
              <span className="dam-chip" aria-live="polite"><b>{numberFmt.format(listing.data.totalMatches)}</b> match “{term}”</span>
            )}
            {!allTicked && totalRuns > 0 && !busy && (
              <button type="button" className="dam-link" onClick={() => { setAllBase(true); setFlipped(new Map()); }}>Tick all</button>
            )}
          </div>
        </header>

        <div className="dam-body" aria-busy={listing.isFetching}>
          {listing.isPending && <p className="dam-state">Looking for runs on day {day}…</p>}
          {listing.isError && !listing.data && (
            <div className="dam-state">
              <p style={{ margin: '0 0 12px', color: BAD }}>{listing.error.message}</p>
              <button type="button" className="dam-btn ghost" onClick={() => listing.refetch()}>Try again</button>
            </div>
          )}
          {listing.data && !rows.length && page === 1 && (
            <p className="dam-state">
              {term
                ? `No athlete matching “${term}” has a run to attach on day ${day}.`
                : `Nothing left to attach on day ${day} · ${dayCaption(day, eventStart, eventTimezone)}.`}
            </p>
          )}
          {rows.length > 0 && (
            <table className="dam-table" style={{ opacity: listing.isPlaceholderData ? 0.55 : 1 }}>
              <colgroup>
                <col style={{ width: 52 }} />
                <col />
                <col style={{ width: 120 }} />
                <col style={{ width: 130 }} />
                <col style={{ width: 120 }} />
                <col className="dam-hide-sm" style={{ width: 110 }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="dam-check" scope="col">
                    <input
                      type="checkbox"
                      aria-label="Tick every run on the day"
                      checked={allTicked}
                      ref={(el) => { if (el) el.indeterminate = selected > 0 && !allTicked; }}
                      onChange={toggleAll}
                      disabled={busy}
                    />
                  </th>
                  <th scope="col">Athlete</th>
                  <th scope="col">Started</th>
                  <th scope="col" className="dam-num">Distance</th>
                  <th scope="col" className="dam-num">Time</th>
                  <th scope="col" className="dam-num dam-hide-sm">Pace /km</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const on = isTicked(row.run.id);
                  const name = nameOf(row, names.data);
                  return (
                    <tr key={row.run.id} className={on ? 'picked' : ''} onClick={() => { if (!busy) toggle(row); }}>
                      <td className="dam-check">
                        <input
                          type="checkbox"
                          aria-label={`${name}, run at ${startedAt(row.run.started_at)}`}
                          checked={on}
                          disabled={busy}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggle(row)}
                        />
                      </td>
                      <td>
                        <span className="dam-who">
                          <span className="dam-avatar" aria-hidden="true">{initialsOf(name)}</span>
                          <span title={name}>{name}</span>
                        </span>
                      </td>
                      <td>{startedAt(row.run.started_at)}</td>
                      <td className="dam-num">{formatKm(row.run.distance_meters)}</td>
                      <td className="dam-num" style={{ fontWeight: 700 }}>{formatClock(row.run.duration_seconds)}</td>
                      <td className="dam-num dam-hide-sm">{formatPace(row.run.duration_seconds, row.run.distance_meters)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <footer className="dam-foot">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontSize: 13, color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
              {rows.length
                ? `Showing ${numberFmt.format(firstShown)}–${numberFmt.format(lastShown)} of ${numberFmt.format(shownTotal)}${term ? ' matches' : ''}`
                : 'No runs to show'}
            </span>
            {pages > 1 && (
              <nav className="dam-pager" aria-label="Pages">
                <button type="button" className="dam-pg" aria-label="Previous page" disabled={page <= 1 || busy} onClick={() => setPage((p) => p - 1)}>‹</button>
                {pageWindow(page, pages).map((n, i) => (n === '…'
                  ? <span key={`gap-${i}`} style={{ padding: '0 4px', color: MUTED }}>…</span>
                  : (
                    <button
                      key={n}
                      type="button"
                      className={`dam-pg${n === page ? ' cur' : ''}`}
                      aria-current={n === page ? 'page' : undefined}
                      disabled={busy}
                      onClick={() => setPage(n)}
                    >
                      {numberFmt.format(n)}
                    </button>
                  )))}
                <button type="button" className="dam-pg" aria-label="Next page" disabled={page >= pages || busy} onClick={() => setPage((p) => p + 1)}>›</button>
              </nav>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginLeft: 'auto' }}>
            <button type="button" className="dam-btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
            <button
              type="button"
              className="dam-btn main"
              onClick={onAttach}
              disabled={busy || selected === 0}
            >
              {busy ? 'Attaching…' : `Attach selected (${numberFmt.format(selected)})`}
            </button>
          </div>
        </footer>

        {showPanel && (
          <AttachProgress
            progress={progress}
            report={report}
            selected={selected}
            onDone={() => setReport(null)}
          />
        )}
      </div>
    </div>
  ), document.body);
}
